import { storageService } from 'acerto-ds-services';
import EventManager from '@/models/EventManager';
import JwtAuthToken from '@/models/JwtAuthToken';
import ServerTimeService from './ServerTimeService';

type AcertoUserHash = string | undefined;

export interface DeeplinkCaptacaoPersistida {
  token?: string;
  typeId?: string;
  id?: string;
  primeiraTentativaDeCadastroRealizada: boolean;
}

export interface AssinaturaPushRegistrada {
  endpoint: string;
  /**
   * `keys.auth`, a prova de posse exigida pela remoção. Fica guardado porque, com a permissão
   * bloqueada, o `getSubscription()` pode não devolver mais a assinatura.
   */
  auth: string;
  instalada: boolean;
  /**
   * `hash` de quem confirmou a assinatura no backend. Ausente em registros gravados antes do campo
   * existir, o que força um reenvio único.
   */
  usuarioHash?: string;
}

/**
 * Obtém o tempo máximo de vida do cache a partir das configurações de ambiente
 */
const getCacheMaxAge = (): number => {
  return Number(appSettings.VUE_APP_VUE_QUERY_STALE_TIME) || 30 * 1000;
};

export default {
  session: new storageService.StorageEntry<JwtAuthToken | null>({
    key: 'session',
    default: null,
    serializer: JwtAuthToken,
  }),

  sessionSS: new storageService.StorageEntry<JwtAuthToken | null>({
    key: 'sessionSS',
    default: null,
    serializer: JwtAuthToken,
    storage: sessionStorage,
  }),

  queryString: new storageService.StorageEntry<string>({
    key: 'queryString',
    default: '',
    storage: sessionStorage,
  }),

  meuacerto: new storageService.StorageEntry<{ popup?: boolean; ativo?: boolean }>({
    key: 'meuacerto',
    default: {},
  }),

  hash: new storageService.StorageEntry<AcertoUserHash>({
    key: 'hash',
    default: undefined,
    serializer: {
      toJSON(obj: AcertoUserHash) {
        if (obj) {
          window.gtag('set', { user_id: obj });
        }

        return JSON.stringify(obj);
      },
    },
  }),

  eventManager: new storageService.StorageEntry<EventManager>({
    key: 'eventManager',
    default: { dividas: [] },
  }),

  confirmacaoPagamentoManager: new storageService.StorageEntry<Record<string, number>>({
    key: 'confirmacaoPagamentoManager',
    default: {},
  }),

  serverTime: new storageService.StorageEntry<{ savedAt?: number; timeDiff: number }>({
    key: 'serverTime',
    default: { timeDiff: 0 },
  }),

  disableEmailProviderBlackList: new storageService.StorageEntry<string>({
    key: 'disableEmailProviderBlackList',
    default: '',
  }),

  skipServerSync: new storageService.StorageEntry<boolean>({
    key: ServerTimeService.storageOverrideKey,
    storage: sessionStorage,
    default: false,
    serializer: {
      fromJSON(json: string) {
        return json !== undefined;
      },
      toJSON(obj: boolean) {
        if (!obj) {
          logger.warn(
            'Aviso: tentativa de salvar `false` no `skipServerSync` storage.\n' +
              '\t-> A maneira correta de fazer isso seria remover a chave usando' +
              ' `storage.remove()`.'
          );
        }

        return 'true';
      },
    },
  }),

  vueQueryCache: new storageService.StorageEntry<{
    timestamp: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queries: any[];
  }>({
    key: 'vueQueryCache',
    storage: sessionStorage,
    default: { timestamp: Date.now(), queries: [] },
  }),

  exibiuLoadingPosLogin: new storageService.StorageEntry<boolean>({
    key: 'exibiuLoadingPosLogin',
    storage: sessionStorage,
    default: false,
  }),

  exibiuToastVencimentoMeusAcordos: new storageService.StorageEntry<string | null>({
    key: 'toastVencimentoMeusAcordosJtiExibido',
    storage: sessionStorage,
    default: null,
  }),

  /**
   * `jti` do login em que o modal da campanha Gift Card já foi exibido.
   * Guardar o `jti` (e não um booleano) garante nova exibição a cada login novo, mesmo do mesmo CPF.
   */
  giftCardModalJtiExibido: new storageService.StorageEntry<string | null>({
    key: 'giftCardModalJtiExibido',
    storage: sessionStorage,
    default: null,
  }),

  /**
   * Login (`jti`) já avaliado no teste `checkout_unica_divida_disponivel` e, quando elegível, a dívida
   * única. O `jti` garante uma avaliação por login, qualquer que seja o resultado; a dívida diz ao
   * checkout, inclusive depois de um F5, quando exibir a experiência do teste.
   */
  checkoutUnicaDividaElegivel: new storageService.StorageEntry<{
    jti: string;
    divida: { api: string; dividaId: number } | null;
  } | null>({
    key: 'checkoutUnicaDividaElegivel',
    storage: sessionStorage,
    default: null,
  }),

  /**
   * Assinatura de push que o backend já confirmou ter persistido. Distingue uma assinatura já
   * entregue de uma que existe só no navegador (ex.: o envio anterior falhou, ou o navegador trocou
   * a assinatura) ou que precisa de novo upsert porque o app foi instalado depois.
   */
  pushAssinatura: new storageService.StorageEntry<AssinaturaPushRegistrada | null>({
    key: 'pushAssinatura',
    default: null,
  }),

  /**
   * Contexto da captação deeplink. O token sai da URL na entrada e, sem isto,
   * o refresh do checkout perde o cadastro e a formalização segue ou estoura.
   */
  deeplinkCaptacao: new storageService.StorageEntry<DeeplinkCaptacaoPersistida | null>({
    key: 'deeplinkCaptacao',
    storage: sessionStorage,
    default: null,
  }),

  /**
   * Verifica se o cache do Vue Query ainda é válido (não expirou)
   */
  isCacheValido(): boolean {
    const cached = this.vueQueryCache.get();
    if (!cached?.queries || cached.queries.length === 0) return false;

    const now = Date.now();
    const cacheAge = now - cached.timestamp;

    return cacheAge <= getCacheMaxAge();
  },
};
